import { useState, type ChangeEvent } from 'react'
import { Download, Paperclip, X } from 'lucide-react'
import { fileDownloadUrl, isFileRef, uploadFile, type FileRef } from '@/api/files'
import { apiErrorMessage } from '@/features/settings/apiErrorMessage'

/** Renders a custom "file" field: upload a file, then show it as a download link with a remove button. */
export default function CustomFileField({
  value,
  onChange,
}: {
  value: unknown
  onChange: (v: FileRef | null) => void
}) {
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const file = isFileRef(value) ? value : null

  async function onPick(e: ChangeEvent<HTMLInputElement>) {
    const f = e.target.files?.[0]
    e.target.value = '' // allow re-selecting the same file after a remove
    if (!f) return
    setBusy(true)
    setError(null)
    try {
      onChange(await uploadFile(f))
    } catch (err) {
      // Show the real reason (storage not configured / too large / auth) instead of always blaming size.
      setError(apiErrorMessage(err))
    } finally {
      setBusy(false)
    }
  }

  async function download() {
    if (!file) return
    try {
      window.open(await fileDownloadUrl(file.key), '_blank', 'noopener')
    } catch {
      setError('Could not open the file.')
    }
  }

  if (file) {
    return (
      <div className="flex items-center gap-2 text-sm">
        <button
          type="button"
          onClick={download}
          className="flex items-center gap-1 text-brand-primary hover:underline min-w-0"
          title="Download"
        >
          <Download size={13} className="shrink-0" />
          <span className="truncate">{file.filename}</span>
        </button>
        <button
          type="button"
          onClick={() => onChange(null)}
          className="text-gray-300 hover:text-danger shrink-0"
          title="Remove file"
        >
          <X size={14} />
        </button>
      </div>
    )
  }

  return (
    <div>
      <label className="inline-flex items-center gap-1.5 text-sm text-brand-primary cursor-pointer hover:underline">
        <Paperclip size={13} />
        {busy ? 'Uploading…' : 'Upload a file'}
        <input type="file" className="hidden" onChange={onPick} disabled={busy} />
      </label>
      {error && <p className="text-danger text-[11px] mt-0.5">{error}</p>}
    </div>
  )
}
